import React, { createContext, useContext, useMemo, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import useSessionWithAuth from '../hooks/useSessionWithAuth';
import { useMessageContext } from './MessageContext';
import { useResourceDispatch, useResourceState } from './ResourceContext';
import { useUI } from './UIContext';
import { useChatSessions } from './ChatSessionContext';
import { useCalendar } from './CalendarContext';
import { useResourceOpener } from '../hooks/useResourceOpener';
import { useConfig } from './ConfigContext';
import { useAppEvents } from './AppEventsContext';
import { useApi } from './ApiContext';
import { getChatSessionDetails } from '../services/chatSessionApi';

const RESOURCE_SYNC_DEBOUNCE_DELAY = 3000;

const SessionContext = createContext({});

export const useSession = () => useContext(SessionContext);

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

export const SessionProvider = ({ children, onResourceSync: onAppResourceSync, sseEventFilter, sessionInitStrategy }) => {
  const { getFormattedMessage } = useMessageContext();
  const { refreshResources, applySyncUpdate } = useResourceDispatch();
  const { getResourceById } = useResourceState();
  const { openResourceTab, activeResourceId, openResources, closeResourceTab } = useUI();
  const { loadChatSessions, applyChatSessionUpdate } = useChatSessions();
  const { loadDates } = useCalendar();
  const { handlers: appEventHandlers } = useAppEvents();
  const { refreshAndOpenResource } = useResourceOpener();
  const { team_name, chatTeamConfigs, startSessionButtons } = useConfig();
  const { resource: resourceApi } = useApi();

  const syncQueueRef = useRef([]);
  const syncTimerRef = useRef(null);
  const isProcessingSyncsRef = useRef(false);

  const onResourceSyncCallbackRef = useRef();
  const onAppEventCallbackRef = useRef();
  const onResourceUpdateNeededCallbackRef = useRef();
  const onNewSessionCreatedCallbackRef = useRef();

  const sessionHookData = useSessionWithAuth({
    onResourceUpdateNeeded: (info) => onResourceUpdateNeededCallbackRef.current?.(info),
    onNewSessionCreated: (id) => onNewSessionCreatedCallbackRef.current?.(id),
    onResourceSync: (event) => onResourceSyncCallbackRef.current?.(event),
    onAppEvent: (event) => onAppEventCallbackRef.current?.(event),
    defaultSessionOptions: { team_name },
    sseEventFilter,
    activeResourceId,
    sessionInitStrategy,
  });

  const { sessionId, sessionContext, teamName, setTeamName, setSessionContext } = sessionHookData;
  const prevSessionId = usePrevious(sessionId);
  const prevTeamName = usePrevious(teamName);

  // Effect to close resources from previous session if they are locked to that session type
  useEffect(() => {
    if (prevSessionId && sessionId !== prevSessionId) {
      console.log(`Chat session changed from ${prevSessionId} to ${sessionId}. Previous team: ${prevTeamName}. Checking for locked resources.`);
      const prevTeamConfig = chatTeamConfigs?.[prevTeamName];
      if (prevTeamConfig?.nonClosableResourceTypes?.length > 0) {
        const lockedResourceTypes = prevTeamConfig.nonClosableResourceTypes;
        console.log(`Previous team had locked resource types:`, lockedResourceTypes);
        openResources.forEach(resource => {
          const resourceType = resource.type || resource.resource_type;
          if (lockedResourceTypes.includes(resourceType)) {
            console.log(`Closing locked resource from previous chat session: ${resourceType} (ID: ${resource.id})`);
            closeResourceTab(resource.id);
          }
        });
      }
    }
  }, [sessionId, prevSessionId, prevTeamName, chatTeamConfigs, openResources, closeResourceTab]);

  const processSyncQueue = useCallback(async () => {
    if (isProcessingSyncsRef.current) {
      console.log("[ResourceSync] Already processing, will be handled in the next cycle.");
      return;
    }
    if (syncQueueRef.current.length === 0) {
      console.log("[ResourceSync] Queue is empty, nothing to process.");
      return;
    }

    isProcessingSyncsRef.current = true;
    const eventsToProcess = [...syncQueueRef.current];
    syncQueueRef.current = [];

    const resourceTypesChanged = new Set(eventsToProcess.map(e => e.resource_type));
    console.log('[ResourceSync] Resource types changed in this batch:', resourceTypesChanged);

    const coreResourceTypes = new Set(['collection', 'unit', 'file', 'reference', 'date']);
    const coreContextsToRefresh = new Set();

    resourceTypesChanged.forEach(type => {
      if (['workout_session', 'workout_plan', 'location', 'equipment', 'exercise_definition', 'location_feature'].includes(type)) {
          return;
      }
      if (coreResourceTypes.has(type)) {
        coreContextsToRefresh.add('general');
      }
      if (type === 'session' || type === 'chat_session') {
        coreContextsToRefresh.add('chat_session');
      }
      if (type === 'date' || type === 'collection') {
        coreContextsToRefresh.add('calendar');
      }
    });

    if (coreContextsToRefresh.has('general')) {
      console.log("[ResourceSync] Triggering global refreshResources() for core types.");
      await refreshResources().catch(e => console.error('[ResourceSync] Error refreshing main resource map:', e));
    }
    if (coreContextsToRefresh.has('calendar')) {
      await loadDates().catch(e => console.error('[ResourceSync] Error refreshing calendar dates:', e));
    }
    if (coreContextsToRefresh.has('chat_session')) {
      await loadChatSessions(false).catch(e => console.error('[ResourceSync] Error refreshing chat sessions:', e));
    }

    if (onAppResourceSync) {
        onAppResourceSync(resourceTypesChanged);
    }

    isProcessingSyncsRef.current = false;
  }, [refreshResources, loadChatSessions, loadDates, onAppResourceSync]);

  const handleResourceSync = useCallback(async (event) => {
    if (!event?.content) return;
    const { resource_type, change_type } = event.content;

    if (resource_type === 'chat_session') {
      if (change_type !== 'deleted') {
        try {
          const fullSession = await getChatSessionDetails(event.content.resource_id);
          applyChatSessionUpdate(fullSession);
        } catch (err) {
          console.error(`[ResourceSync] Failed to fetch and apply update for chat_session ${event.content.resource_id}`, err);
        }
      }
    } else {
      await applySyncUpdate(event);
    }

    // If current session context updated
    if (
        event?.content?.resource_type === 'chat_session' &&
        event?.content?.change_type === 'updated' &&
        event?.content?.context_added &&
        event?.session_id === sessionId
    ) {
        const newContextItem = event.content.context_added;
        setSessionContext(prevContext => {
            if (prevContext.some(item => item.id === newContextItem.id)) {
                return prevContext;
            }
            return [...prevContext, ...newContextItem];
        });
    }

    syncQueueRef.current.push(event.content);

    if (syncTimerRef.current) return;

    processSyncQueue();
    syncTimerRef.current = setTimeout(() => {
      syncTimerRef.current = null;
      if (syncQueueRef.current.length > 0) {
          processSyncQueue();
      }
    }, RESOURCE_SYNC_DEBOUNCE_DELAY);
  }, [processSyncQueue, sessionId, setSessionContext, applySyncUpdate, applyChatSessionUpdate]);

  const mapPayloadToContext = (payload) => {
    const context = [];
    if (!payload) return context;
    Object.entries(payload).forEach(([key, value]) => {
      if (key === 'team_name') return;
      if (key.endsWith('_id') && typeof value === 'string') {
        const type = key.replace('_id', '');
        context.push({ id: value, type: type });
      } else {
        const stringVal = typeof value === 'string' ? value : JSON.stringify(value);
        const noteContent = (key === 'notes' || key === 'instruction')
            ? stringVal
            : `${key.replace(/_/g, ' ')}: ${stringVal}`;
        context.push({
            id: `event_metadata_${key}`,
            type: 'metadata',
            data: { [key]: value },
            notes: noteContent
        });
      }
    });
    return context;
  };

  const handleAppEvent = useCallback((event) => {
    if (!event?.content?.event_name) {
      console.warn('[SessionContext] Received invalid APP_EVENT:', event);
      return;
    }
    const { event_name, payload } = event.content;

    const customHandler = appEventHandlers[event_name];
    if (customHandler) {
      const handlerContext = {
        addTimelineItem: sessionHookData.addTimelineItem,
        openResourceTab,
        sessionId: sessionHookData.sessionId,
        initializeSession: sessionHookData.initializeSession
      };
      if (customHandler(event, handlerContext) === true) return;
    }

    if (event_name === 'switch_team') {
        const targetTeam = payload?.team_name;
        if (!targetTeam) {
            console.warn("[SessionContext] switch_team event missing 'team_name' in payload.");
            return;
        }
        console.log(`[SessionContext] Handling switch_team to '${targetTeam}'`);
        const initialContext = mapPayloadToContext(payload);
        const buttonConfig = startSessionButtons?.find(btn => btn.teamName === targetTeam);
        const preChatConfig = buttonConfig?.preChatConfig || null;

        sessionHookData.initializeSession(null, {
            team_name: targetTeam,
            context: initialContext,
            preChatConfig: preChatConfig
        });
    }
  }, [appEventHandlers, sessionHookData, openResourceTab, startSessionButtons]);

  const handleResourceUpdateNeeded = useCallback((resourceInfo) => {
    if (resourceInfo?.to !== 'User') return;
    let resourceId = null;
    if (resourceInfo.resourceInfo?.id) {
        resourceId = resourceInfo.resourceInfo.id;
    } else if (resourceInfo.id) {
        resourceId = resourceInfo.id;
    } else if (typeof resourceInfo.content === 'string') {
        const match = resourceInfo.content.match(/\[RESOURCE\](.*?)\[\/RESOURCE\]/);
        if (match?.[1]) resourceId = String(match[1].trim());
    }

    if (resourceId) {
        refreshAndOpenResource(resourceId);
    }
  }, [refreshAndOpenResource]);

  const handleNewSessionCreated = useCallback((newSessionId) => {
    console.log(`[SessionContext] New session initialized: ${newSessionId}. Waiting for persistence (first message) to sync list.`);
  }, []);

  useEffect(() => {
    onResourceSyncCallbackRef.current = handleResourceSync;
    onAppEventCallbackRef.current = handleAppEvent;
    onResourceUpdateNeededCallbackRef.current = handleResourceUpdateNeeded;
    onNewSessionCreatedCallbackRef.current = handleNewSessionCreated;
  });

  useEffect(() => () => {
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
  }, []);

  // Auto-open resources from context
  useEffect(() => {
    if (!sessionId || !chatTeamConfigs) return;

    const handleAutoOpenAndTeamInference = async () => {
      let effectiveTeamName = teamName;
      
      // Infer team if missing
      if (!effectiveTeamName && sessionContext?.length > 0) {
        for (const team in chatTeamConfigs) {
          const config = chatTeamConfigs[team];
          if (config.autoOpenResourceTypes?.some(type => sessionContext.some(item => item.type === type))) {
            effectiveTeamName = team;
            setTeamName(team);
            break;
          }
        }
      }

      if (!effectiveTeamName) return;

      const teamConfig = chatTeamConfigs[effectiveTeamName];
      if (!teamConfig?.autoOpenResourceTypes?.length || !sessionContext?.length) return;

      for (const contextResource of sessionContext) {
        if (teamConfig.autoOpenResourceTypes.includes(contextResource.type)) {
          if (openResources.some(res => res.id === contextResource.id)) continue;
          
          try {
            let resourceToOpen = getResourceById(contextResource.id);
            if (!resourceToOpen) {
              resourceToOpen = await resourceApi.fetchResource(contextResource.type, contextResource.id);
            }
            // CHANGED: Pass { keepMinimized: true } to prevent the panel from popping up on auto-open
            if (resourceToOpen) openResourceTab(resourceToOpen, { keepMinimized: true });
          } catch (error) {
            console.error(`[AutoOpen] Error opening resource ${contextResource.type}:${contextResource.id}:`, error);
          }
        }
      }
    };

    const timer = setTimeout(handleAutoOpenAndTeamInference, 100);
    return () => clearTimeout(timer);
  }, [sessionId, sessionContext, teamName, chatTeamConfigs, openResources, getResourceById, openResourceTab, resourceApi, setTeamName]);

  const sendMessageWithContext = useCallback(async (content, contextOverride = null) => {
    if (!sessionHookData.sessionId || !content?.trim() || sessionHookData.isSending || !sessionHookData.isConnected) {
      return false;
    }
    let messageToSend = contextOverride ? `${contextOverride} ${content}` : (getFormattedMessage ? getFormattedMessage(content) : content);
    return sessionHookData.sendMessage(messageToSend);
  }, [sessionHookData, getFormattedMessage]);

  const contextValue = useMemo(() => ({
    ...sessionHookData,
    sendMessage: sendMessageWithContext,
  }), [sessionHookData, sendMessageWithContext]);

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
};

SessionProvider.propTypes = {
  children: PropTypes.node.isRequired,
  onResourceSync: PropTypes.func,
  sseEventFilter: PropTypes.func,
  sessionInitStrategy: PropTypes.func,
};

export default SessionContext;